import moment from "moment";
import get from "lodash/get";
import find from "lodash/find";
import chain from "lodash/chain";
import defaults from "lodash/defaults";
import getDayRangeString from "../../../utils/day-range-string";
import ShareModal from "../../../parts/modals/share";
import CalendarAddExerciseModal from "../../../parts/modals/calendar-add-exercise";
import ProgressionModal from "../../../parts/modals/progression";
import CalendarDay from "./CalendarDay";
import JWPlayerVideoModal from "../../../parts/modals/jwplayer-video";
import VideoRecordTutorialModal from "../../../parts/modals/VideoRecordTutorialModal";
import SwapExerciseModal from "../../../parts/modals/swap-exercise";

import CoachRecordVideoModal from "../../../parts/modals/coach-record-video";

// Constants
import { DATE_FORMATS } from "../../../constants";
import { dayProgramProjection } from "../../../utils/projection-helpers";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";

const { DATE_DAY_FORMAT, UTC } = DATE_FORMATS;

const CalendarDayController = async (
  { req, res, locals, load, api, fail },
  next
) => {
  if (req.query.view !== "day") return next();
  const { query = {} } = req;
  // locals.athlete whose calendar is being rendered. This has been preloaded in the ensureCoach middleware
  const units = locals.athlete ? locals.athlete.units : locals.units;
  const modal = get(query, "modal", {});

  defaults(query, {
    units,
    date: moment.utc(query.date).format(DATE_DAY_FORMAT),
    zoneType: "total",
  });

  locals.originalQueryDate = "" + query.date;
  locals.activeWeek = moment(query.date, DATE_DAY_FORMAT).startOf("week");

  if (query.date instanceof Date)
    query.date = moment.utc(query.date).format(DATE_DAY_FORMAT);
  const startDate = moment.utc(query.date, DATE_DAY_FORMAT).startOf("day");
  const endDate = moment.utc(query.date, DATE_DAY_FORMAT).endOf("day");

  await Promise.all([
    load("activeExercises", {
      start: startDate,
      end: endDate,
      proxyAthlete: query.athlete,
      reqSource: "calendar",
    }),
    load("currentYearExercises", {
      start: startDate,
      end: endDate,
      proxyAthlete: query.athlete,
      reqSource: "calendar",
    }),
    load("workoutNote", locals.originalQueryDate, query.athlete),
    load(
      "workoutCoachNote",
      locals.originalQueryDate,
      query.athlete,
      query.organizationCoach || locals.activeUser._id
    ),
    load("videoRecord", locals.originalQueryDate, query.athlete, "calendar"),
    load("dayPersonalRecords", {
      end: moment
        .utc(locals.query.date, DATE_DAY_FORMAT)
        .endOf("day")
        .format(UTC),
      proxyAthlete: query.athlete,
    }),
    load("dailyStats", {
      proxyAthlete: query.athlete,
      date: locals.originalQueryDate,
    }),
    load("nutritionPlans", {
      proxyAthlete: query.athlete,
    }),
  ]);

  if (locals.activeExercises) {
    const exerciseWithCycle = locals.activeExercises.find((e) => e.cycle) || {};
    locals.cycleDetail = exerciseWithCycle.cycle;
    const cycleId = get(locals, "cycleDetail.id", false);
    if (cycleId)
      await Promise.all([
        load("editCycle", {
          cycle: cycleId,
          proxyAthlete: query.athlete,
          customProjection: dayProgramProjection,
        }),
        load("editExercises", {
          cycle: cycleId,
          isScheduled: true,
          proxyAthlete: query.athlete,
          fields: ["cycle", "date", "_id", "type"],
        }),
      ]);
  }

  if (modal.type === "share" && !modal.snapshot) {
    // create share link.
    try {
      modal.snapshot = (
        await (
          await api("snapshot", {
            method: "POST",
            body: {
              type: "workout",
              data: JSON.stringify(
                chain(locals)
                  .pick(["activeExercises"])
                  .assign({ start: startDate.toJSON(), end: endDate.toJSON() })
                  .value()
              ),
            },
          })
        ).json()
      )._id;

      modal.date = getDayRangeString([startDate, endDate]);

      return res.refreshWith({ modal });
    } catch (e) {
      console.error(e.stack || e);
      fail(400, "Error creating share link.");
    }
  }

  const editExercise = query.editMode
    ? find(locals.activeExercises, { _id: query.exercise })
    : {};

  const getCoachVideoRecord = () => {
    if (locals.videoRecord) return locals.videoRecord;
    return null;
  };

  const isAllowToViewAllOrganizationsAthletes =
    locals.organizationRoleName &&
    (locals.organizationRoleName === "Owner" ||
      locals.organizationRoleName === "Admin");
  if (
    locals.activeUser.organizationId &&
    isAllowToViewAllOrganizationsAthletes &&
    !locals.allAthletesFromOrganization
  ) {
    await load("allAthletesFromOrganization");
  }
  let comesFromPR = false;

  comesFromPR = req?.original?.headers?.referer?.includes("personal-record");
  locals.modal = {
    "exercise-set-video": (
      <JWPlayerVideoModal
        mediaId={query.mediaId}
        originalId={query.originalId}
        athleteId={query.uploaderId || query.athlete}
        exerciseToEdit={query.exerciseToEdit}
        reviewedByTheCoach={query.reviewedByTheCoach}
        setIndex={query.setIndex}
        videoIndex={query.videoIndex}
      />
    ),
    "calendar-add-exercise": (
      <CalendarAddExerciseModal
        editExercise={editExercise}
        reqSource="calendar"
      />
    ),
    share: <ShareModal />,
    progression: <ProgressionModal />,
    "video-record-tutorial": <VideoRecordTutorialModal />,
    "swap-exercise": <SwapExerciseModal exercise={editExercise} />,
    "coach-record-video": (
      <CoachRecordVideoModal videoRecord={getCoachVideoRecord()} />
    ),
    "activity-feed": <ActivityDrawer />,
  }[modal.type];
  res.body = (
    <CalendarDay
      comesFromPR={comesFromPR}
      editedExercise={query.editExerciseId}
      setIndex={query.setIndex}
    />
  );
};

export default CalendarDayController;
